import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Archive, Save, Play, Lock, Clock, AlertTriangle } from 'lucide-react'
// Fixed: Replaced Freeze with Lock icon
import { useToast } from '@/hooks/use-toast'

interface Organization {
  id: string;
  name: string;
}

interface RetentionArchiveTabProps {
  organization: Organization;
  onSave: (data: any) => void;
}

export function RetentionArchiveTab({ organization, onSave }: RetentionArchiveTabProps) {
  const { toast } = useToast()
  
  const [retention, setRetention] = useState({
    closed_cases: 24, // månader
    documents: 24,
    logs: 12,
    comments_actions: 24
  })

  const [legalHold, setLegalHold] = useState({
    enabled: false,
    cases: [] as string[],
    reason: '',
    expires_at: ''
  })

  const [scheduledDeletion, setScheduledDeletion] = useState({
    enabled: true,
    next_run: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 days from now
    frequency: 'monthly',
    time: '02:00'
  })

  const handleSave = () => {
    onSave({ retention, legalHold, scheduledDeletion })
  }

  const simulateDeletion = async () => {
    toast({
      title: "Simulering påbörjad",
      description: "Simuleringen visar vilka data som skulle tas bort enligt nuvarande inställningar.",
    })
    
    // Simulate API call
    setTimeout(() => {
      toast({
        title: "Simulering klar",
        description: "47 stängda ärenden, 234 dokument och 1,205 loggposter skulle tas bort.",
      })
    }, 2000)
  }

  const freezeCase = () => {
    toast({
      title: "Legal Hold aktiverat",
      description: "Ärendet har markerats för juridisk bevarande och kommer inte gallras.",
    })
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Archive className="h-5 w-5" />
            Gallringsregler
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Ange hur länge olika typer av data ska bevaras innan automatisk gallring
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="closed_cases">Stängda ärenden (månader)</Label>
              <Input
                id="closed_cases"
                type="number"
                value={retention.closed_cases}
                onChange={(e) => setRetention(prev => ({ ...prev, closed_cases: parseInt(e.target.value) }))}
                min="1"
                max="120"
              />
              <p className="text-xs text-muted-foreground mt-1">Standard: 24 månader</p>
            </div>
            
            <div>
              <Label htmlFor="documents">Dokument (månader)</Label>
              <Input
                id="documents"
                type="number"
                value={retention.documents}
                onChange={(e) => setRetention(prev => ({ ...prev, documents: parseInt(e.target.value) }))}
                min="1"
                max="120"
              />
              <p className="text-xs text-muted-foreground mt-1">Standard: 24 månader</p>
            </div>
            
            <div>
              <Label htmlFor="logs">Systemloggar (månader)</Label>
              <Input
                id="logs"
                type="number"
                value={retention.logs}
                onChange={(e) => setRetention(prev => ({ ...prev, logs: parseInt(e.target.value) }))}
                min="1"
                max="60"
              />
              <p className="text-xs text-muted-foreground mt-1">Standard: 12 månader</p>
            </div>
            
            <div>
              <Label htmlFor="comments_actions">Kommentarer & åtgärder (månader)</Label>
              <Input
                id="comments_actions"
                type="number"
                value={retention.comments_actions}
                onChange={(e) => setRetention(prev => ({ ...prev, comments_actions: parseInt(e.target.value) }))}
                min="1"
                max="120"
              />
              <p className="text-xs text-muted-foreground mt-1">Standard: 24 månader</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="h-5 w-5" />
            Legal Hold (Juridisk bevarande)
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Skydda specifika ärenden från automatisk gallring
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-base font-medium">Aktivera Legal Hold</Label>
              <p className="text-sm text-muted-foreground">Förhindra gallring av utvalda ärenden</p>
            </div>
            <Switch
              checked={legalHold.enabled}
              onCheckedChange={(checked) => setLegalHold(prev => ({ ...prev, enabled: checked }))}
            />
          </div>

          {legalHold.enabled && (
            <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
              <div>
                <Label htmlFor="hold_reason">Anledning</Label>
                <Input
                  id="hold_reason"
                  value={legalHold.reason}
                  onChange={(e) => setLegalHold(prev => ({ ...prev, reason: e.target.value }))}
                  placeholder="Ange anledning för bevarande"
                />
              </div>
              
              <div>
                <Label htmlFor="hold_expires">Utgår (valfritt)</Label>
                <Input
                  id="hold_expires"
                  type="date"
                  value={legalHold.expires_at}
                  onChange={(e) => setLegalHold(prev => ({ ...prev, expires_at: e.target.value }))}
                />
              </div>

              <Button variant="outline" onClick={freezeCase} className="gap-2">
                <Lock className="h-4 w-4" />
                Frys ärende
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Schemalagd gallring
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-base font-medium">Automatisk gallring</Label>
              <p className="text-sm text-muted-foreground">Kör gallring enligt schema</p>
            </div>
            <Switch
              checked={scheduledDeletion.enabled}
              onCheckedChange={(checked) => setScheduledDeletion(prev => ({ ...prev, enabled: checked }))}
            />
          </div>

          {scheduledDeletion.enabled && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="frequency">Frekvens</Label>
                <Select 
                  value={scheduledDeletion.frequency} 
                  onValueChange={(value) => setScheduledDeletion(prev => ({ ...prev, frequency: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="weekly">Veckovis</SelectItem>
                    <SelectItem value="monthly">Månadsvis</SelectItem>
                    <SelectItem value="quarterly">Kvartalsvis</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="time">Tid</Label>
                <Input
                  id="time"
                  type="time"
                  value={scheduledDeletion.time}
                  onChange={(e) => setScheduledDeletion(prev => ({ ...prev, time: e.target.value }))}
                />
              </div>
            </div>
          )}

          <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg">
            <AlertTriangle className="h-4 w-4 text-blue-600" />
            <span className="text-sm text-blue-800">
              Nästa schemalagda gallring: {scheduledDeletion.next_run} kl {scheduledDeletion.time}
            </span>
          </div>

          <div className="flex gap-2">
            <Button variant="outline" onClick={simulateDeletion} className="gap-2">
              <Play className="h-4 w-4" />
              Simulera gallring
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleSave} className="gap-2">
          <Save className="h-4 w-4" />
          Spara gallringsinställningar
        </Button>
      </div>
    </div>
  )
}